import {Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {
    AbstractBackOfficeComponent,
    Actor,
    AdvSignModalComponent,
    AdvSignModalModel,
    Circuit,
    CircuitEvent,
    CircuitFilter,
    CircuitStatus,
    DataPagingOrchestrator,
    DataPagingOrchestratorBuilder,
    DateRange,
    DateRangeUtils,
    DefaultCancelCircuitProvider,
    DefaultPerformTaskProvider,
    DefaultState,
    FilterDropdownItem,
    FilterDropdownItemBuilder,
    GroupType,
    IconAdapterService,
    NotificationType,
    PesAlertModel,
    PesAlertModelBuilder,
    PesBackOfficeDateFilterComponent,
    PesBackOfficeFilterComponent,
    PesCircuitSearchService,
    PesDossierService,
    PesGroup,
    PesModalService,
    PesNotificationService,
    PesPromptModel,
    PesPromptModelBuilder,
    PesTask,
    PesTaskService,
    Rights,
    Signatory,
    Strings,
    SubscriptionManager,
    Transaction
} from '@pes/ui';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs/Observable';
import {first} from 'rxjs/operators/first';
import {filter} from 'rxjs/operators/filter';
import {delay} from 'rxjs/operators/delay';
import {finalize} from 'rxjs/operators/finalize';
import {map} from 'rxjs/operators/map';
import {debounceTime, mergeMap} from 'rxjs/operators';
import {ISubscription} from 'rxjs/Subscription';
import {LbpaCircuitViewUtils} from '../../utils/lbpa-circuit-view.utils';
import {NgbDate} from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date';
import {LbpaProfile} from '../../config/provider/lbpa-profile.constant';

/**
 * Displays the list of circuits available for the current user.
 */
@Component({
    selector: 'pes-circuits-front-office',
    templateUrl: './circuits-front-office.component.html'
})
export class CircuitsFrontOfficeComponent extends AbstractBackOfficeComponent implements OnInit, OnDestroy {

    /**
     * @inheritDoc
     */
    @Input() public backButtonTooltip: string = 'back_office.back_button_label';
    /**
     * Event fired when an action is triggered.
     */
    @Output() public change: EventEmitter<CircuitEvent> = new EventEmitter<CircuitEvent>();

    protected emptyMessageTxt: string = null;

    /**
     * The reference to the DataPagingOrchestrator instance for this view.
     */
    protected dataPagingOrchestrator: DataPagingOrchestrator<Circuit> = null;
    /**
     * The list of items displayed in the filter dropdown.
     */
    protected dropdownItems: FilterDropdownItem[] = [
        FilterDropdownItemBuilder.build('fo_circuits.list.column_devis_contrat', 'meta.devisContrat', true),
        FilterDropdownItemBuilder.build('fo_circuits.list.column_name', 'data.circuitStatus.title', true),
        FilterDropdownItemBuilder.build('fo_circuits.list.bgp', 'data.circuitStatus.technicalContext.agency.keyword', true),
        FilterDropdownItemBuilder.build('fo_circuits.list.column_id_rh', 'data.circuitStatus.technicalContext.idrh', true),
        FilterDropdownItemBuilder.build('fo_circuits.list.subscriber', 'data.circuitStatus.actors.assignee.lastName', true)
    ];
    /**
     * The list of items displayed in the status filter dropdown.
     */
    protected statusDropdownItems: FilterDropdownItem[] = [
        FilterDropdownItemBuilder.build('circuit.status.STARTED', CircuitStatus.STARTED, true),
        FilterDropdownItemBuilder.build('circuit.status.FINISHED', CircuitStatus.FINISHED, false),
        FilterDropdownItemBuilder.build('circuit.status.CANCELLED', CircuitStatus.CANCELLED, false),
        FilterDropdownItemBuilder.build('circuit.status.EXPIRED', CircuitStatus.EXPIRED, false)
    ];
    /**
     * An adapter that changes icons references depending on the specified types.
     */
    protected iconAdapter: IconAdapterService = null;
    /**
     * The map for the ngCollapse element on each circuit.
     */
    protected circuitCollapseMap: Map<string, boolean> = null;
    /**
     * Reference of the pesFilter form component.
     */
    @ViewChild('pesFilter') protected pesFilter: PesBackOfficeFilterComponent<Circuit>;
    /**
     * Reference of the pesFilter form component.
     */
    @ViewChild('pesDateFilter') protected pesDateFilter: PesBackOfficeDateFilterComponent;
    @ViewChild('table') protected table: any;
    protected expanded: any = {};
    /**
     * The CircuitFilter object used to filter circuit search.
     */
    private _filter: CircuitFilter = null;
    /**
     * The list of query fields used to filter user search.
     */
    private _queryFields: string [] = null;
    /**
     * The reference to the PesCircuitSearchService singleton.
     */
    private readonly _circuitSearchService: PesCircuitSearchService = null;
    /**
     * The reference to the PesDossierService singleton.
     */
    private readonly _dossierService: PesDossierService = null;
    /**
     * The reference to the PesModalService singleton.
     */
    private readonly _modal: PesModalService = null;
    /**
     * The reference to the TranslateService singleton.
     */
    private readonly _translate: TranslateService = null;
    /**
     * The reference to the PES task service.
     */
    private readonly _taskService: PesTaskService = null;
    /**
     * Reference of PES notifications service.
     */
    private readonly _notify: PesNotificationService = null;
    /**
     * The default implementation for the PerformTaskProvider interface.
     */
    private readonly _performTaskProvider: DefaultPerformTaskProvider = null;
    /**
     * The default implementation for the CancelCircuitProvider interface.
     */
    private readonly _defaultCancelCircuitProvider: DefaultCancelCircuitProvider = null;
    /**
     * The sorting properties.
     */
    private _sorts: any[] = null;
    /**
     * Fires event each ti me user enters a new search token.
     */
    private readonly _tokenHandler: EventEmitter<string> = new EventEmitter<string>();
    /**
     * Delays triggering of the PES user search query when the user types text into the search input.
     */
    private _searchDebounce: Observable<string> = null;
    /**
     * The token used for elasticsearch query.
     */
    private _token: string = null;

    /**
     * Types de contrats a filtrer dans l'interface
     */
    private contractTypeToExclude = [`AUTO`, `GAV-INDIV`, `MRH2`, `PJ`, `ASCOL`];

    /**
     * Creates a new PesCircuitsComponent instance.
     *
     * @param {Injector} injector the reference to the Angular injector.
     */
    constructor(protected injector: Injector) {
        super(injector);
        this._circuitSearchService = injector.get(PesCircuitSearchService);
        this._dossierService = injector.get(PesDossierService);
        this._modal = injector.get(PesModalService);
        this._translate = injector.get(TranslateService);
        this.iconAdapter = injector.get(IconAdapterService);
        this._taskService = injector.get(PesTaskService);
        this._notify = injector.get(PesNotificationService);
        this._performTaskProvider = injector.get(DefaultPerformTaskProvider);
        this._defaultCancelCircuitProvider = injector.get(DefaultCancelCircuitProvider);
        this.breadcrumbItems = [{label: 'fo_circuits.list.title'}];
        SubscriptionManager.getInstance().createSubscribable(this);
    }

    /**
     * @inheritDoc
     */
    public ngOnInit(): void {
        this._sorts = [
            {
                prop: 'meta.expirationDate',
                dir: 'asc',
                type: 'date'
            }
        ];
        this._queryFields = [
            'meta.devisContrat',
            'data.circuitStatus.title',
            'data.circuitStatus.technicalContext.idrh',
            'data.circuitStatus.technicalContext.agency.keyword',
            'data.circuitStatus.actors.assignee.lastName',
            'data.circuitStatus.actors.assignee.firstName'
        ];

        if (!this.displayBgp()) {
            this.dropdownItems = this.dropdownItems.filter(i => i.label !== 'fo_circuits.list.bgp');
            this._queryFields = this._queryFields.filter(q => q !== 'data.circuitStatus.technicalContext.agency.keyword');
        }

        this._searchDebounce = this._tokenHandler.pipe(debounceTime(500));
        const subscription: ISubscription = this._searchDebounce.subscribe(() => this.refresh());
        SubscriptionManager.getInstance().registerSubscription(this, subscription);
        this.initDataPaging();
        this.initFilter();
        this.refresh();
        this.circuitCollapseMap = new Map<string, boolean>();
    }

    /**
     * @inheritDoc
     */
    public ngOnDestroy(): void {
        SubscriptionManager.getInstance().deleteSubscribable(this);
    }

    /**
     * Updates the list of circuits.
     */
    public refresh(): void {
        super.refresh();
        this.loading = true;
        Object.assign(this._filter, {
            from: this.dataPagingOrchestrator.from,
            size: this.dataPagingOrchestrator.size,
            queryFields: this._queryFields,
            searchQuery: this._token,
            must: null,
            sorts: this._sorts
        });
        const divisions: PesGroup[] = this.account.identity.features.groups.filter(group => group.type === GroupType.DIVISION);
        if (this.account.hasRight(Rights.CIRCUIT_LIST_DIVISION) && divisions && divisions.length > 0) {
            this._filter.groupsOr = divisions.map(group => group.name);
        }

        this._filter.context = {
            healthData: false,
        };

        if (!this.account.hasAnyProfile(`developer`, `platform_admin`)) {
            this._filter.mustNot = [
                {
                    terms: {
                        'data.circuitStatus.context.contractType.keyword': this.contractTypeToExclude,
                    },
                },
            ];
        }


        this.saveStateCircuitFilter(this._filter);

        setTimeout(() => {
            const subscription: ISubscription = this._circuitSearchService
                .searchCircuits(this._filter)
                .pipe(
                    finalize(() => {
                        this.emptyMessageTxt = this._translate.instant('fo_circuits.dossierDataTable.noData');
                        this.loading = false;
                    })
                ).subscribe(page => {
                    this.dataPagingOrchestrator.setPage(page);
                });
            SubscriptionManager.getInstance().registerSubscription(this, subscription);
        }, 1000);
    }

    /**
     * Deletes the current circuit.
     */
    public remove(event: MouseEvent, circuit: Circuit): void {
        event.preventDefault();
        event.stopImmediatePropagation();
        const model: PesAlertModel = PesAlertModelBuilder.build(
            'circuit.dialog.delete.label',
            'circuit.dialog.delete.delete_confirm',
            NotificationType.DANGER
        );
        const subscription: ISubscription = this._modal
            .confirm(model)
            .subscribe(yes => {
                if (yes) {
                    this.loading = true;
                    this._dossierService.delete(circuit.dossier.reference)
                        .subscribe(() => {
                            this.refresh();
                        });
                }
            });
        SubscriptionManager.getInstance().registerSubscription(this, subscription);
    }

    protected displayBgp(): boolean {
        return this.account.hasProfile(LbpaProfile.CONSEILLER_ADMIN);
    }

    /**
     * Invoked each time the user changes sorting within the datagrid.
     *
     * @param {any} event the data associated with the sort event.
     */
    protected doSort(event: any): void {
        this._sorts[0].dir = event.newValue;
        let prop: string = Strings.EMPTY_STRING;
        let type: string = Strings.EMPTY_STRING;
        switch (event.column.prop) {
            case 'expirationDate':
                prop = 'meta.expirationDate';
                type = 'date';
                break;
            case 'dossier.meta.status' :
                prop = 'meta.status';
                break;
            case 'title':
                prop = 'data.circuitStatus.title';
                break;
            case 'idrh' :
                prop = 'data.circuitStatus.technicalContext.idrh';
                break;
            case 'bgp' :
                prop = 'data.circuitStatus.technicalContext.agency';
                break;
            case 'devisContrat' :
                prop = 'meta.devisContrat';
                break;
        }
        this._sorts[0].prop = prop;
        this._sorts[0].type = type;
        this.dataPagingOrchestrator.setPageOffset(0, false);
        this._tokenHandler.emit(this._token);
    }

    /**
     * Invoked each time the user clicks a template row in the view table.
     *
     * @param {Circuit} circuit the reference to the template associated with the selected row.
     */
    protected manageCircuit(circuit: Circuit): void {
        this.state.go(
            DefaultState.FO_CIRCUITS_DETAILS,
            {
                circuitRef: circuit.id
            }
        );
    }

    /**
     * Custom filter function.
     */
    protected filterFunction(token: string, _filterComponent: PesBackOfficeFilterComponent<Circuit>): Circuit[] {
        this._token = (token !== Strings.EMPTY_STRING) ? token : null;
        this._tokenHandler.pipe(
            map(() => {
                return false;
            })
        );
        // Allows to reset the initial cursot position in the datagrid pager.
        this.dataPagingOrchestrator.setPageOffset(0, false);
        this._tokenHandler.emit(this._token);
        this.saveStateCircuitFilter(this._filter);
        return null;
    }

    /**
     * Event handler dispatched each time the user changes filter options.
     */
    protected filterStatusChange(filterTypeList: FilterDropdownItem[]): void {
        this.setStatusFilter(filterTypeList);
        this.dataPagingOrchestrator.setPageOffset(0, false);
        this.refresh();
    }

    /**
     * Fills the date range filter.
     *
     * @param {DateRange} dateRange the list of status filter items used to create the search filter.
     */
    protected dateFilterChange(dateRange: DateRange): void {
        const minDate: Date = DateRangeUtils.ngbDateToDate(dateRange.minDate);
        const maxDate: Date = DateRangeUtils.ngbDateToDate(dateRange.maxDate, 23, 59, 59);
        this._filter.expired = [minDate, maxDate];
        this.dataPagingOrchestrator.setPageOffset(0, false);
        this.refresh();
    }

    /**
     * Event handler dispatched each time the user changes filter options.
     */
    protected filterTypeChange(filterTypeList: FilterDropdownItem[]): void {
        this._queryFields.splice(0);
        filterTypeList.forEach((item: FilterDropdownItem) => {
            if (item.selected) {
                const field: string = item.field;
                if (field === 'data.circuitStatus.actors.assignee.lastName') {
                    this._queryFields.push('data.circuitStatus.actors.assignee.firstName');
                }
                this._queryFields.push(field);
            }
        });
        this.dataPagingOrchestrator.setPageOffset(0, false);
        this._tokenHandler.emit(this._token);
        this.saveStateCircuitFilter(this._filter);
    }

    /**
     * Invoked each time the user clicks on the start circuit button.
     */
    protected newCircuit(): void {
        this.state.go(DefaultState.BO_TEMPLATES);
    }

    /**
     * Returns the status icons for the specified circuit.
     *
     * @param {Circuit} circuit the circuit for which to get the icons.
     * @return {string} the status icons for the specified circuit.
     */
    protected getCircuitIcon(circuit: Circuit): string {
        const icons: string = LbpaCircuitViewUtils.getStatusIcon(circuit, this._translate) +
            LbpaCircuitViewUtils.getExpirationFlag(circuit, this._translate);
        return icons;
    }

    /**
     * Show the action button, if there's available actions (based on profile, etc...)
     *
     * @param {Circuit} circuit
     */
    protected showButtons(circuit: Circuit): boolean {
        return circuit.dossier.taskList.length > 0
            || (circuit.dossier.attachments.length && this.account.hasRight(Rights.CIRCUIT_DOWNLOAD_VIEW))
            || (circuit.advSign && !this.isPresential(circuit) && circuit.dossier.meta.status === CircuitStatus.STARTED)
            || this.showCancel(circuit);
    }

    /**
     * Open the action button container on pad device and close all others action button container.
     *
     * @param {string} idCircuit the id of the circuit clicked.
     */
    protected openActionButtons(idCircuit: string): void {
        const circuitCollapsed = this.circuitCollapseMap.get(idCircuit);
        this.circuitCollapseMap.forEach((_collapsed, id) => {
            this.circuitCollapseMap.set(id, false);
        });
        this.circuitCollapseMap.set(idCircuit, !circuitCollapsed);
    }

    /**
     * Return true if the action butotn container is collapsed (open).
     *
     * @param {string} idCircuit the id of the circuit clicked.
     */
    protected isCircuitCollapsed(idCircuit: string): boolean {
        return this.circuitCollapseMap.get(idCircuit);
    }

    /**
     * Displaying cancel button
     *
     * @param {Circuit} circuit
     * @returns {boolean}
     */
    protected showCancel(circuit: Circuit): boolean {
        return circuit.dossier.meta.status === CircuitStatus.STARTED &&
            this.account.hasRight(Rights.CIRCUIT_CANCEL) &&
            this._defaultCancelCircuitProvider.isNotCancelable(circuit, this.account);
    }

    /**
     * Return if the current task use the presential option.
     *
     * @returns {boolean}
     */
    protected isPresential(circuit: Circuit): boolean {
        const actorId: string = circuit.workflow.currentTask.actor;
        const currentTaskId: string = circuit.workflow.currentTask.id;

        // Search current task
        const currentTask: PesTask = circuit.dossier.taskList.find((task: PesTask) => {
            return task.data.id === currentTaskId;
        });

        if (!currentTask || !currentTask.data.taskData.signatories) {
            return false;
        }

        // Search current signatory
        const signatory: Signatory = currentTask.data.taskData.signatories.find((item: Signatory) => {
            return item.actorId === actorId;
        });

        return signatory && signatory.user.presential;
    }

    /**
     * Show specific action button for the task and current circuit
     *
     * @param {PesTask} task
     * @param {Circuit} circuit
     */
    protected showActionButton(task: PesTask, circuit: Circuit) {
        return task.nickname === 'notify'
            && task.data.userInfo
            && task.data.userInfo.id !== this.account.identity.id
            && this.account.hasRight(Rights.CIRCUIT_NOTIFY)
            && circuit.dossier.meta.status !== CircuitStatus.EXPIRED;
    }

    /**
     * Open a modal window to notify the actor of the current task.
     *
     * @param {PesTask} task the reference to the task for which to notify the actor.
     */
    protected notifyActor(task: PesTask) {
        const model: PesPromptModel = PesPromptModelBuilder.build(
            'fo_circuits.list.actions.confirm_notify',
            this._translate.instant('fo_circuits.list.actions.reminder_actor_email', task.data.userInfo),
            task.data.userInfo.email
        );
        const subscription: ISubscription = this._modal
            .prompt(model)
            .pipe(
                filter(email => email != null),
                first(() => this.loading = true),
                mergeMap(email => this._taskService.submit(task.reference, {email: email})),
            ).subscribe(() => {
                this._notify.success('fo_circuits.list.actions.successfully_notified');
                CircuitEvent.create(CircuitEvent.NOTIFY);
                this.refresh();
            });
        SubscriptionManager.getInstance().registerSubscription(this, subscription);
    }

    /**
     * Displaying task button
     *
     * @param {PesTask} task
     * @param {Circuit} circuit
     * @returns {boolean}
     */
    protected showTask(task: PesTask, circuit: Circuit): boolean {
        return task.data.steps &&
            this.account.hasRight(Rights.CIRCUIT_UPDATE) &&
            circuit.dossier.meta.status !== CircuitStatus.EXPIRED &&
            this._performTaskProvider.canPerformTask(this.account, circuit, task);
    }

    /**
     * Go to the task of the circuit
     *
     * @param {PesTask} task
     */
    protected goToTask(task: PesTask): void {
        this.state.go(DefaultState.TASK, {taskReference: task.reference, stepIdx: 0, docIdx: 0});
    }

    /**
     * Cancel the circuit.
     *
     * @param {Circuit} circuit
     */
    protected cancel(_event: MouseEvent, circuit: Circuit): void {
        const model: PesPromptModel = PesPromptModelBuilder.build(
            'circuit.cancel.label',
            'circuit.dialog.cancel.reason',
            null,
            NotificationType.DANGER,
            null,
            255,
            'circuit.dialog.cancel.submit',
            'circuit.dialog.cancel.cancel'
        );
        const subscription: ISubscription = this._modal
            .prompt(model)
            .pipe(
                filter(reason => reason != null),
                first(() => this.loading = true),
                mergeMap(reason =>
                    this._dossierService.cancel(circuit.dossier.reference, 'status:CANCELLED', 'cancel_circuit',
                        {result: {reason: reason}, user: this.account.identity})),
                delay(1000)
            )
            .subscribe(() => {
                    this.change.emit(CircuitEvent.create(CircuitEvent.CANCELLED, circuit));
                    this.refresh();
                },
                error => {
                    this._notify.error(`pes.errors.${error.code}`);
                    this.refresh();
                }
            );
        SubscriptionManager.getInstance().registerSubscription(this, subscription);
    }

    /**
     * Open the modal to update the Advanced Signature Token
     *
     * @param {Circuit} circuit circuit where to update the Advanced Signature Token
     */
    protected openAdvSignModal(circuit: Circuit): void {
        const adbSignModalModel: AdvSignModalModel =
            new AdvSignModalModel(circuit.dossier.reference, this.getSignatories(circuit));
        const subscription: ISubscription = this._modal.dialog(AdvSignModalComponent, adbSignModalModel).subscribe();
        SubscriptionManager.getInstance().registerSubscription(this, subscription);
    }

    /**
     * Display or not the current task
     *
     * @param {Circuit} circuit
     */
    protected showCurrentTask(circuit: Circuit): boolean {
        const status: string = circuit.dossier.meta.status;
        return circuit.workflow && (status !== CircuitStatus.CANCELLED &&
            status !== CircuitStatus.FINISHED &&
            status !== CircuitStatus.EXPIRED);
    }

    /**
     * Returns the reference of the specified circuit.
     *
     * @param {Circuit} circuit the circuit for which to return the reference.
     * @return {string} the reference of the specified circuit.
     */
    protected getCircuitRef(circuit: Circuit): string {
        const data: any = circuit.dossier.document;
        return data.contractRef || circuit.id;
    }


    // protected getCircuitIdRh(circuit: Circuit): string {
    //     const data: any = circuit.dossier.document;
    //     return data.circuitStatus.technicalContext.idrh;
    // }

    protected getSubscriber(circuit: Circuit): string {
        const subscriber = circuit.actors.find(actor => actor.id === 'subscriber');
        return subscriber.assignee.lastName + ' ' + subscriber.assignee.firstName;
    }

    protected toggleExpandRow(row: any): void {
        this.table.rowDetail.toggleExpandRow(row);
    }

    /**
     * Initializes data paging functionalities.
     */
    private initDataPaging(): void {
        this.dataPagingOrchestrator = DataPagingOrchestratorBuilder.build<Circuit>();
        const subscription: ISubscription =
            this.dataPagingOrchestrator.pageOffsetChanged.subscribe(() => this.refresh());
        SubscriptionManager.getInstance().registerSubscription(this, subscription);
    }

    private initFilter(): void {
        const restoreFilter: boolean = this.state.params.restoreFilter === 'true';
        this._filter = this._filter ? this._filter : restoreFilter ? this.getStateCircuitFilter() : new CircuitFilter();
        if (restoreFilter) {
            this.restore();
        } else {
            this.initStatus();
        }
    }

    /**
     * Initializes the current status filter, depending on the status parameter in the URL.
     */
    private initStatus(): void {
        const status: string = this.state.params.status;
        if (status) {
            this.statusDropdownItems.forEach((item: FilterDropdownItem) => {
                item.selected = item.field === status;
            });
        }
        this.setStatusFilter(this.statusDropdownItems);
    }

    private restore(): void {
        this.statusDropdownItems.forEach((item: FilterDropdownItem) => {
            item.selected = this._filter.status.indexOf(item.field) > -1;
        });

        this.dropdownItems.forEach((item: FilterDropdownItem) => {
            item.selected = this._filter.queryFields.indexOf(item.field) > -1;
        });
        this._queryFields = this._filter.queryFields;

        if (this._filter.updated && this._filter.updated.length > 0) {
            this.pesDateFilter.timeFilterVisible = true;
            if (this._filter.updated[0]) {
                const beginDate = new Date(this._filter.updated[0]);
                this.pesDateFilter.date1 = NgbDate.from({
                    year: beginDate.getFullYear(),
                    month: beginDate.getMonth() + 1,
                    day: beginDate.getDate()
                });
            }

            if (this._filter.updated[1]) {
                const endDate = new Date(this._filter.updated[1]);
                this.pesDateFilter.date2 = NgbDate.from({
                    year: endDate.getFullYear(),
                    month: endDate.getMonth() + 1,
                    day: endDate.getDate()
                });
            }

        }
        if (this._filter.searchQuery) {
            this.pesFilter.input.nativeElement.value = this._filter.searchQuery;
            this.filterFunction(this._filter.searchQuery, null);
        }
    }

    /**
     * Fills the status filter.
     *
     * @param {FilterDropdownItem[]} filterTypeList the list of status filter items used to create the search filter.
     */
    private setStatusFilter(filterTypeList: FilterDropdownItem[]): void {
        const status: string[] = [];
        filterTypeList.forEach((item: FilterDropdownItem) => {
            if (item.selected) {
                status.push(item.field);
            }
        });
        this._filter.status = status;
    }

    /**
     * Get all the signatories of a circuit
     *
     * @param {Circuit} circuit
     */
    private getSignatories(circuit: Circuit): Actor[] {
        const actorsId: Set<string> = new Set<string>();
        circuit.transactions.forEach((tr: Transaction) => {
            Set.prototype.add.apply(actorsId, tr.signatories);
        });
        return circuit.actors.filter((actor: Actor) => {
            return actorsId.has(actor.id);
        });
    }

    /**
     * Save config filter to the local storage
     */
    private saveStateCircuitFilter(circuitFilter: CircuitFilter): void {
        window.localStorage['circuitFilter'] = JSON.stringify(circuitFilter);
    }

    /**
     * Load config filter from the local storage
     */
    private getStateCircuitFilter(): CircuitFilter {
        if (window.localStorage['circuitFilter']) {
            return JSON.parse(window.localStorage['circuitFilter']);
        }
        return new CircuitFilter();
    }
}



// WEBPACK FOOTER //
// ./src/app/component/circuit-list-front-office/circuits-front-office.component.ts